/**
 * Fatiado de pages/Menu.tsx por responsabilidade (#131, R3).
 */
import { useState } from 'react'
import { currencyFor } from './shared.js'
import type { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { products as prodApi, type Product } from '../../lib/api.js'
import { Button, Input, toast } from '../../components/ui/index.js'
import { formatCents, parseCents } from '../../lib/utils.js'
import { useI18n } from '../../lib/i18n.js'
// ─── Extras editor ────────────────────────────────────────────────────────────

export function ExtrasEditor({
  product,
  locale,
  currency = '',
  priceLocale = '',
  qc,
  onProductUpdate,
}: {
  product: Product
  locale: string
  /** #428: moeda do PAÍS do tenant; `currencyFor(locale)` é último recurso */
  currency?: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
  qc: ReturnType<typeof useQueryClient>
  onProductUpdate: (p: Product) => void
}) {
  const { t } = useI18n()
  const [newName, setNewName] = useState('')
  const [price, setPrice] = useState('')
  const [priceType, setPriceType] = useState<'fixed' | 'percent'>('fixed')

  const addMut = useMutation({
    mutationFn: () =>
      prodApi.addExtra(product.id, {
        name: newName.trim(),
        price_type: priceType,
        price_value:
          priceType === 'fixed'
            ? parseCents(price)
            : Math.round(parseFloat(price.replace(',', '.')) * 100),
      }),
    onSuccess: (extra) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      setNewName('')
      setPrice('')
      onProductUpdate({ ...product, extras: [...product.extras, extra] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const delMut = useMutation({
    mutationFn: (id: string) => prodApi.deleteExtra(product.id, id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate({ ...product, extras: product.extras.filter((e) => e.id !== id) })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-700 mb-3">{t('admin.menu.extras')}</p>
      <div className="space-y-2 mb-3">
        {product.extras.length === 0 && (
          <p className="text-xs text-gray-400">{t('admin.menu.no_extras')}</p>
        )}
        {product.extras.map((ex) => (
          <div
            key={ex.id}
            className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
          >
            <span className="text-sm">{ex.name}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-indigo-700">
                {ex.price_type === 'fixed'
                  ? formatCents(
                      ex.price_value,
                      currency || currencyFor(locale),
                      priceLocale || locale,
                    )
                  : `+${(ex.price_value / 100).toFixed(0)}%`}
              </span>
              <button
                data-testid={`catalog-extra-delete-${ex.id}`}
                onClick={() => delMut.mutate(ex.id)}
                className="text-gray-400 hover:text-red-500 text-xs"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          placeholder={t('admin.menu.extra_ph')}
          data-testid="catalog-extra-new-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="flex-1 min-w-[120px]"
        />
        <select
          value={priceType}
          onChange={(e) => setPriceType(e.target.value as 'fixed' | 'percent')}
          className="rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="fixed">{currency || currencyFor(locale)}</option>
          <option value="percent">%</option>
        </select>
        <Input
          placeholder={priceType === 'fixed' ? '3,50' : '10'}
          data-testid="catalog-extra-new-price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-20"
        />
        <Button
          size="sm"
          variant="secondary"
          data-testid="catalog-extra-add"
          loading={addMut.isPending}
          onClick={() => addMut.mutate()}
          disabled={!newName.trim() || !price}
        >
          +
        </Button>
      </div>
    </div>
  )
}
